import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
} from "recharts";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "ui/components/chart";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "ui/components/card";
import { BARCHART } from "icons";
import { Icon } from "components";
import { formatHoursToTime } from "utils/timesheet";
import ChartCardSkeleton from "./chart-loader";

const chartConfig = {
    logged_hours: {
        label: "Logged",
        color: "#BAE6FD",
    },
    approved_hours: {
        label: "Approved",
        color: "#047857",
    },
};

export function ProjectTypeBarChart({ data, isLoading, error, title }) {

    const transformData = (data) => {
        if (!Array.isArray(data)) return [];
        const groupedData = data.reduce((acc, item) => {
            const employeeId = item.employee?.id;
            if (!employeeId) return acc;
            if (!acc[employeeId]) {
                acc[employeeId] = {
                    name: `${item.employee?.first_name || ""}`,
                    logged_hours: 0,
                    approved_hours: 0,
                };
            }
            acc[employeeId].logged_hours += item.logged_hours || 0;
            acc[employeeId].approved_hours += item.approved_hours || 0;
            return acc;
        }, {});

        return Object.values(groupedData)
            .filter(item => item.logged_hours > 0 || item.approved_hours > 0)
    };

    const chartData = data && !isLoading ? transformData(data) : [];

    if (isLoading) {
        return (
            <ChartCardSkeleton />
        );
    }

    if (error) {
        return (
            <Card className="mx-6 mt-4 border p-0">
                <CardHeader className="p-0 border-b">
                    <CardTitle className="p-3 flex items-center gap-2 text-sm font-medium">
                        <Icon icon={BARCHART} className="size-4 bg-white" />
                        <div className="text-muted-foreground">
                            {title}
                        </div>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="text-center py-8 text-destructive">
                        Error loading data: {error?.message || error?.toString() || "Something went wrong"}
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!chartData || chartData.length === 0) {
        return null;
        // (
        //     <Card className="mx-6 mt-4 mb-6 p-0 gap-0 border-1 radius-xl">
        //         <CardHeader className="flex flex-row items-center justify-between !p-4 border-b bg-white">
        //             <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-700">
        //                 <Icon icon={BARCHART} className="size-4 bg-white" />
        //                 <div className="text-muted-foreground">
        //                     {title}
        //                 </div>
        //             </CardTitle>
        //         </CardHeader>
        //         <CardContent>
        //             <div className="flex h-[300px] items-center justify-center text-muted-foreground">
        //                 No data available
        //             </div>
        //         </CardContent>
        //     </Card>
        // );
    }

    // Calculate max value for Y-axis
    const maxValue = Math.max(
        ...chartData.flatMap(item => [item.logged_hours, item.approved_hours])
    );
    const yAxisMax = Math.ceil(maxValue * 1.1 / 50) * 50;

    // Generate Y-axis ticks
    const generateYAxisTicks = (max) => {
        const step = 50;
        const ticks = [];
        for (let i = 0; i <= max; i += step) {
            ticks.push(i);
        }
        return ticks;
    };

    const yAxisTicks = generateYAxisTicks(yAxisMax);

    return (
        <Card className="mx-6 mt-4 mb-6 p-0 gap-0 border-1 shadow-none">
            <CardHeader className="flex flex-row items-center justify-between !p-4 border-b bg-white">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Icon icon={BARCHART} className="size-4" />
                    <div className="text-muted-foreground">
                        {title}
                    </div>
                </CardTitle>

                <div className="flex gap-6 text-xs">
                    <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-xs" style={{ backgroundColor: '#BAE6FD' }}></span>
                        Logged
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-xs" style={{ backgroundColor: '#047857' }}></span>
                        Approved
                    </span>
                </div>
            </CardHeader>

            <CardContent className="p-0 bg-white overflow-x-auto">
                <div
                    className="min-w-full"
                    style={{ width: `${chartData.length * 80}px`, height: "320px" }}
                >
                    <ChartContainer config={chartConfig} className="w-full h-full">
                        <BarChart
                            data={chartData}
                            margin={{ left: 10, right: 20, top: 20, bottom: 10 }}
                            barCategoryGap="15%"
                            barGap={2}
                            width={chartData.length * 80}
                            height={320}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#E5E7EB"
                            />
                            <XAxis
                                dataKey="name"
                                tickLine={false}
                                axisLine={false}
                                fontSize={12}
                                tickMargin={10}
                                stroke="#6B7280"
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                fontSize={12}
                                domain={[0, yAxisMax]}
                                ticks={yAxisTicks}
                                stroke="#6B7280"
                            />
                            <ChartTooltip
                                content={
                                    <ChartTooltipContent
                                        hideLabel
                                        formatter={(value, name) => [
                                            `${formatHoursToTime(value)} `,
                                            chartConfig[name]?.label || name,
                                        ]}
                                    />
                                }
                            />
                            <Bar
                                dataKey="logged_hours"
                                fill="#BAE6FD"
                                radius={[4, 4, 0, 0]}
                                maxBarSize={30}
                            />
                            <Bar
                                dataKey="approved_hours"
                                fill="#047857"
                                radius={[4, 4, 0, 0]}
                                maxBarSize={30}
                            />
                        </BarChart>
                    </ChartContainer>
                </div>
            </CardContent>

        </Card>
    );
}